import { api } from "../../app/api";
import { Options } from "../../common/types/requestOptions";
import { EventPayload } from "../../common/types/Event";
import {
  JobAdCreated,
  JobAdEdited,
  JobAdIntegrationPayload,
  JobAdPublished,
  JobAdReOpened,
} from "./types/events";
import {
  JobAd,
  JobAdIntegration,
  JobAdIntegrationDetails,
  JobAdIntegrationType,
} from "./types/common";
import { nanoid } from "@reduxjs/toolkit";
import { Paginated } from "../../common/types/Paginated";

export type EditJobAdPayload = JobAd & {
  jobAdId: string;
  version: number;
};

export interface JobAdsFilters {
  search: string;
  filters: {
    cursor?: string;
    pageSize?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    location?: string;
    hasOrder?: string;
    contactPerson?: string;
    customerOrgName?: string;
    orderQueueId?: string;
    campaigns?: string;
  };
  pageSize?: string;
  cursor?: string;
}

export interface FetchGetJobAdIdByBundleId {
  jobAdId: string;
}

export type JobAdFetch = JobAd & {
  version: number;
  type: string;
  integrations: {
    id: string;
    integration: JobAdIntegrationDetails;
    type: JobAdIntegrationType;
    version: number;
  }[];
};

interface OccupationsCategoryFilters {
  language: string;
}

interface OccupationCategory {
  id: string;
  name: string;
  occupations: any[];
}

export const getJobAds = (
  filters: JobAdsFilters,
  { requestId }: Options = {},
) => {
  const filterObject = filters.filters;
  const params: Record<string, string> = {};
  if (filters.search.toString()) params["search"] = filters.search.toString();
  if (filters.cursor) params["cursor"] = filters.cursor;
  for (const [key, value] of Object.entries(filterObject)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }
  return api.get<Paginated<JobAd[]>>("/projections/qry/jobAds", {
    requestId,
    auth: true,
    queryParams: { ...params, pageSize: filters?.pageSize || "50" },
  });
};

export const getJobAdById = async (
  id: string,
  { requestId }: Options = {},
): Promise<JobAdFetch> => {
  return await api
    .get<JobAdFetch>("/projections/qry/jobAd", {
      requestId,
      auth: true,
      queryParams: { id },
    })
    .then((res) => res.data);
};

export const getJobAdIdByBundleId = async (
  bundleId: string,
  { requestId }: Options = {},
): Promise<FetchGetJobAdIdByBundleId> => {
  return await api
    .get<FetchGetJobAdIdByBundleId>("/projections/qry/jobAdIdByBundleId", {
      requestId,
      auth: true,
      queryParams: { bundleId },
    })
    .then((res) => res.data);
};

export const createNewJobAd = async (
  data: JobAd,
  { requestId }: Options,
): Promise<EventPayload<JobAdCreated>> => {
  return await api
    .post<EventPayload<JobAdCreated>[]>("/recruitment/cmd/createJobAd", {
      requestId,
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data[0]);
};

export const postEditJobAd = async (
  data: EditJobAdPayload,
  { requestId }: Options,
): Promise<EventPayload<JobAdEdited>> => {
  return await api
    .post<EventPayload<JobAdEdited>[]>("/recruitment/cmd/changeJobAd", {
      requestId,
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data[0]);
};

export const postCreateJobAdIntegration = async (
  data: JobAdIntegration,
  { requestId }: Options,
): Promise<EventPayload<JobAdIntegrationPayload>> => {
  return await api
    .post<EventPayload<JobAdIntegrationPayload>[]>(
      "/recruitment/cmd/createJobAdIntegration",
      {
        requestId,
        auth: true,
        body: {
          integration: {
            ...data,
          },
        },
      },
    )
    .then((res) => res.data[0]);
};

export const postEditJobAdIntegration = async (
  data: JobAdIntegration,
  { requestId }: Options,
): Promise<EventPayload<JobAdIntegrationPayload>> => {
  return await api
    .post<EventPayload<JobAdIntegrationPayload>[]>(
      "/recruitment/cmd/changeJobAdIntegration",
      {
        requestId,
        auth: true,
        body: {
          ...data,
        },
      },
    )
    .then((res) => res.data[0]);
};

export type VersionedJobAdPayload = { id: string; version: number };

export const postPublishJobAd = async (
  data: VersionedJobAdPayload,
  { requestId }: Options,
): Promise<EventPayload<JobAdPublished | JobAdIntegrationPayload>[]> => {
  return await api
    .post<EventPayload<JobAdPublished | JobAdIntegrationPayload>[]>(
      "/recruitment/cmd/publishJobAd",
      {
        requestId,
        auth: true,
        body: data,
      },
    )
    .then((res) => res.data);
};

export const postCloseJobAd = async (
  data: VersionedJobAdPayload,
  { requestId }: Options,
) => {
  return await api
    .post("/recruitment/cmd/closeJobAd", {
      requestId,
      auth: true,
      body: data,
    })
    .then((res) => res.data);
};

export const postReOpenJobAd = async (
  data: VersionedJobAdPayload,
  { requestId }: Options,
): Promise<EventPayload<JobAdReOpened>[]> => {
  return await api
    .post<EventPayload<JobAdReOpened>[]>("/recruitment/cmd/reOpenJobAd", {
      requestId,
      auth: true,
      body: data,
    })
    .then((res) => res.data);
};

export const getJoblyOccupationsCategories = async (
  filters: OccupationsCategoryFilters,
): Promise<OccupationCategory[]> => {
  return await api
    .get<OccupationCategory[]>("/recruitment/qry/occupationsCategories", {
      requestId: nanoid(),
      auth: true,
      queryParams: { ...filters },
    })
    .then((res) => res.data);
};
